import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { qualificationFlowApi } from '@/lib/api';
import type {
  FlowValidationError,
  QualificationFlow,
  QualificationNode,
  QualificationSingleSelectNode,
  QualificationSingleSelectOption,
} from '@/types/qualification-flow';
import { AlertCircle, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';

const MAX_SINGLE_SELECT_OPTIONS = 25;

function normalizeId(id: string) {
  return id.trim();
}

function referencedBy(nodes: QualificationNode[], targetId: string): string[] {
  const refs: string[] = [];
  for (const n of nodes) {
    if (n.type === 'yes_no') {
      if (n.next.yes === targetId || n.next.no === targetId) refs.push(n.id);
    }
    if (n.type === 'single_select') {
      if (n.options.some((o) => o.nextNodeId === targetId)) refs.push(n.id);
      if (n.allowFreeText && n.freeTextNextNodeId === targetId) refs.push(n.id);
    }
  }
  return refs;
}

function replaceIdEverywhere(flow: QualificationFlow, fromId: string, toId: string): QualificationFlow {
  const nextNodes = flow.nodes.map((n) => {
    if (n.id === fromId) return { ...n, id: toId } as QualificationNode;

    if (n.type === 'yes_no') {
      return {
        ...n,
        next: {
          yes: n.next.yes === fromId ? toId : n.next.yes,
          no: n.next.no === fromId ? toId : n.next.no,
        },
      };
    }

    if (n.type === 'single_select') {
      return {
        ...n,
        options: n.options.map((o) => ({
          ...o,
          nextNodeId: o.nextNodeId === fromId ? toId : o.nextNodeId,
        })),
        freeTextNextNodeId:
          n.allowFreeText && n.freeTextNextNodeId === fromId ? toId : n.freeTextNextNodeId,
      };
    }

    return n;
  });

  return {
    ...flow,
    startNodeId: flow.startNodeId === fromId ? toId : flow.startNodeId,
    nodes: nextNodes,
  };
}

function validateFlow(flow: QualificationFlow): FlowValidationError[] {
  const errors: FlowValidationError[] = [];
  const ids = flow.nodes.map((n) => n.id);
  const idSet = new Set<string>();

  for (const id of ids) {
    if (idSet.has(id)) errors.push({ type: 'DUPLICATE_NODE_ID', id, message: `Duplicate step ID: ${id}` });
    idSet.add(id);
  }

  if (!idSet.has(flow.startNodeId)) {
    errors.push({ type: 'START_NODE_MISSING', message: `First question "${flow.startNodeId}" does not exist` });
  }

  for (const node of flow.nodes) {
    if (node.type === 'yes_no') {
      if (!node.next?.yes || !node.next?.no) {
        errors.push({ type: 'MISSING_BRANCH', id: node.id, message: `Step ${node.id} is missing Yes or No routing` });
      }
      if (node.next?.yes && !idSet.has(node.next.yes)) {
        errors.push({ type: 'MISSING_REFERENCE', fromId: node.id, toId: node.next.yes, branch: 'yes', message: `Step ${node.id} → Yes points to missing step ${node.next.yes}` });
      }
      if (node.next?.no && !idSet.has(node.next.no)) {
        errors.push({ type: 'MISSING_REFERENCE', fromId: node.id, toId: node.next.no, branch: 'no', message: `Step ${node.id} → No points to missing step ${node.next.no}` });
      }
      continue;
    }

    if (node.type === 'single_select') {
      if (!Array.isArray(node.options) || node.options.length < 1 || node.options.length > MAX_SINGLE_SELECT_OPTIONS) {
        errors.push({
          type: 'SINGLE_SELECT_OPTIONS_COUNT',
          id: node.id,
          message: `Step ${node.id} must have between 1 and ${MAX_SINGLE_SELECT_OPTIONS} options`,
        });
      }

      node.options.forEach((opt, index) => {
        if (!opt.value?.trim() || !opt.label?.trim()) {
          errors.push({
            type: 'SINGLE_SELECT_OPTION_INVALID',
            id: node.id,
            optionIndex: index,
            message: `Step ${node.id} option #${index + 1} needs both value and label`,
          });
        }
        if (!opt.nextNodeId || !idSet.has(opt.nextNodeId)) {
          errors.push({
            type: 'MISSING_REFERENCE',
            fromId: node.id,
            toId: opt.nextNodeId || '(empty)',
            branch: 'option',
            message: `Step ${node.id} option "${opt.label || opt.value || index + 1}" points to a missing step`,
          });
        }
      });

      if (node.allowFreeText) {
        if (!node.freeTextNextNodeId || !idSet.has(node.freeTextNextNodeId)) {
          errors.push({
            type: 'MISSING_REFERENCE',
            fromId: node.id,
            toId: node.freeTextNextNodeId || '(empty)',
            branch: 'free_text',
            message: `Step ${node.id} allows free text but doesn't have a valid next step`,
          });
        }
      }
      continue;
    }

    if (!node.terminalAction) {
      errors.push({ type: 'TERMINAL_ACTION_MISSING', id: node.id, message: `Final step ${node.id} is missing an outcome` });
    }
    if (node.terminalAction === 'redirect_support' && !node.terminalMessage?.trim()) {
      errors.push({ type: 'TERMINAL_MESSAGE_MISSING', id: node.id, message: `Final step ${node.id} must include a message when outcome is "Send to support ticket"` });
    }
  }

  return errors;
}

function createStarterFlow(): QualificationFlow {
  return {
    startNodeId: 'q1',
    nodes: [
      { id: 'q1', type: 'yes_no', text: 'Are you currently a student at Americation?', next: { yes: 'q2', no: 'q2' } },
      {
        id: 'q2',
        type: 'single_select',
        text: 'Which program are you interested in?',
        options: [
          { value: 'cna', label: 'CNA', nextNodeId: 't_continue' },
          { value: 'phlebotomy', label: 'Phlebotomy', nextNodeId: 't_continue' },
        ],
        allowFreeText: true,
        freeTextNextNodeId: 't_continue',
      },
      { id: 't_continue', type: 'terminal', text: 'Thanks for sharing.', terminalAction: 'continue_to_chat', terminalMessage: 'Great — you can start chatting now.' },
      { id: 't_support', type: 'terminal', text: 'Support redirection.', terminalAction: 'redirect_support', terminalMessage: 'Please login and create a support ticket so our team can assist you.' },
    ],
  };
}

export default function QualificationFlowPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flow, setFlow] = useState<QualificationFlow>(createStarterFlow());
  const [serverVersion, setServerVersion] = useState<number | null>(null);

  const errors = useMemo(() => validateFlow(flow), [flow]);
  const isValid = errors.length === 0;
  const nodeIds = useMemo(() => flow.nodes.map((n) => n.id), [flow.nodes]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await qualificationFlowApi.getActive();
      if (res.statusCode !== 200) throw new Error((res as any).message || 'Failed to load flow');
      const f = res.data.flow as QualificationFlow | null;
      if (!f) {
        setFlow(createStarterFlow());
        setServerVersion(null);
        return;
      }
      setFlow({ startNodeId: f.startNodeId, nodes: (f.nodes || []) as QualificationNode[] });
      setServerVersion(f.version ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load flow');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateNode = (idx: number, next: QualificationNode) => {
    setFlow((prev) => {
      const nodes = [...prev.nodes];
      nodes[idx] = next;
      return { ...prev, nodes };
    });
  };

  const addYesNo = () => {
    const id = `q${flow.nodes.filter((n) => n.type === 'yes_no').length + 1}`;
    const unique = nodeIds.includes(id) ? `${id}_${Date.now()}` : id;
    const fallbackNext = flow.nodes.find((n) => n.type === 'terminal')?.id || flow.startNodeId;
    setFlow((prev) => ({
      ...prev,
      nodes: [...prev.nodes, { id: unique, type: 'yes_no', text: 'New yes/no question', next: { yes: fallbackNext, no: fallbackNext } }],
    }));
  };

  const addSingleSelect = () => {
    const id = `q${flow.nodes.filter((n) => n.type !== 'terminal').length + 1}`;
    const unique = nodeIds.includes(id) ? `${id}_${Date.now()}` : id;
    const fallbackNext = flow.nodes.find((n) => n.type === 'terminal')?.id || flow.startNodeId;
    setFlow((prev) => ({
      ...prev,
      nodes: [
        ...prev.nodes,
        {
          id: unique,
          type: 'single_select',
          text: 'New choose-one question',
          options: [{ value: 'option_1', label: 'Option 1', nextNodeId: fallbackNext }],
          allowFreeText: false,
        },
      ],
    }));
  };

  const addTerminal = (action: 'continue_to_chat' | 'redirect_support') => {
    const base = action === 'continue_to_chat' ? 't_continue' : 't_support';
    const unique = nodeIds.includes(base) ? `${base}_${Date.now()}` : base;
    setFlow((prev) => ({
      ...prev,
      nodes: [
        ...prev.nodes,
        {
          id: unique,
          type: 'terminal',
          text: action === 'continue_to_chat' ? 'Allow chat.' : 'Send to support ticket.',
          terminalAction: action,
          terminalMessage:
            action === 'continue_to_chat'
              ? 'Great — you can start chatting now.'
              : 'Please login and create a support ticket so our team can assist you.',
        },
      ],
    }));
  };

  const handleRenameId = (idx: number, nextIdRaw: string) => {
    const nextId = normalizeId(nextIdRaw);
    setFlow((prev) => {
      const current = prev.nodes[idx];
      if (!current || !nextId || current.id === nextId) return prev;
      return replaceIdEverywhere(prev, current.id, nextId);
    });
  };

  const canDelete = (id: string) => referencedBy(flow.nodes, id).length === 0 && flow.startNodeId !== id;

  const deleteNode = (idx: number) => {
    setFlow((prev) => {
      const node = prev.nodes[idx];
      if (!node) return prev;
      const refs = referencedBy(prev.nodes, node.id);
      if (refs.length > 0) {
        toast.error(`Cannot delete: used by ${refs.join(', ')}`);
        return prev;
      }
      if (prev.startNodeId === node.id) {
        toast.error('Cannot delete the first question. Change first question first.');
        return prev;
      }
      return { ...prev, nodes: prev.nodes.filter((_, i) => i !== idx) };
    });
  };

  const updateSingleSelectOption = (
    node: QualificationSingleSelectNode,
    optionIndex: number,
    patch: Partial<QualificationSingleSelectOption>
  ) => {
    const options = node.options.map((o, i) => (i === optionIndex ? { ...o, ...patch } : o));
    return { ...node, options };
  };

  const addSingleSelectOption = (idx: number, node: QualificationSingleSelectNode) => {
    if (node.options.length >= MAX_SINGLE_SELECT_OPTIONS) return;
    const fallbackNext = flow.nodes.find((n) => n.type === 'terminal')?.id || flow.startNodeId;
    updateNode(idx, {
      ...node,
      options: [
        ...node.options,
        {
          value: `option_${node.options.length + 1}`,
          label: `Option ${node.options.length + 1}`,
          nextNodeId: fallbackNext,
        },
      ],
    });
  };

  const removeSingleSelectOption = (idx: number, node: QualificationSingleSelectNode, optionIndex: number) => {
    if (node.options.length <= 1) return;
    updateNode(idx, { ...node, options: node.options.filter((_, i) => i !== optionIndex) });
  };

  const save = async () => {
    const v = validateFlow(flow);
    if (v.length) {
      toast.error(`Fix ${v.length} issue(s) before publishing`);
      return;
    }
    setSaving(true);
    try {
      const res = await qualificationFlowApi.upsert({ startNodeId: flow.startNodeId, nodes: flow.nodes as any[] });
      if (res.statusCode !== 200) throw new Error((res as any).message || 'Publish failed');
      const saved = res.data.flow as QualificationFlow;
      setServerVersion(saved.version ?? null);
      toast.success(`Published${saved.version ? ` (Version ${saved.version})` : ''}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Failed to load flow</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => void load()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-56" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lead chat questions</h1>
          <p className="text-muted-foreground">Set up the question flow shown before a lead can start chat.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{serverVersion ? `Version ${serverVersion}` : 'Not published yet'}</Badge>
          <Button variant="outline" size="sm" onClick={() => void load()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Reload flow
          </Button>
          <Button size="sm" disabled={!isValid || saving} onClick={() => void save()} className="gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save & publish'}
          </Button>
        </div>
      </div>

      {!isValid && (
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-red-700">Please fix these items</CardTitle>
            <CardDescription>These issues must be fixed before you can publish.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {errors.map((e, i) => (
              <div key={i} className="text-red-700">
                - {e.message}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">First question</CardTitle>
          <CardDescription>Select where this flow should begin.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <Select value={flow.startNodeId} onValueChange={(v) => setFlow((prev) => ({ ...prev, startNodeId: v }))}>
            <SelectTrigger className="sm:max-w-[320px]">
              <SelectValue placeholder="Select first question" />
            </SelectTrigger>
            <SelectContent>
              {nodeIds.map((id) => (
                <SelectItem key={id} value={id}>
                  {id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={addYesNo} className="gap-2">
              <Plus className="h-4 w-4" />
              Add yes/no question
            </Button>
            <Button variant="outline" size="sm" onClick={addSingleSelect} className="gap-2">
              <Plus className="h-4 w-4" />
              Add choose-one option question
            </Button>
            <Button variant="outline" size="sm" onClick={() => addTerminal('continue_to_chat')}>
              Add final step (Allow chat)
            </Button>
            <Button variant="outline" size="sm" onClick={() => addTerminal('redirect_support')}>
              Add final step (Send to support)
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {flow.nodes.map((node, idx) => {
          const refs = referencedBy(flow.nodes, node.id);
          const deleteDisabled = !canDelete(node.id);
          const deleteReason =
            flow.startNodeId === node.id
              ? 'This is the first question'
              : refs.length
                ? `Used by: ${refs.join(', ')}`
                : '';

          return (
            <Card key={`${node.id}-${idx}`}>
              <CardHeader className="pb-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">
                      {node.type === 'yes_no'
                        ? 'Yes/No question'
                        : node.type === 'single_select'
                          ? 'Choose-one option question'
                          : 'Final step'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">Step</span>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Button variant="outline" size="sm" className="gap-2" disabled={deleteDisabled} onClick={() => deleteNode(idx)}>
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {deleteDisabled && deleteReason ? <TooltipContent sideOffset={6}>{deleteReason}</TooltipContent> : null}
                  </Tooltip>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Step ID (editable)</p>
                    <Input value={node.id} onChange={(e) => handleRenameId(idx, e.target.value)} placeholder="q1 or final_chat" />
                    {refs.length > 0 && <p className="text-xs text-muted-foreground mt-1">Used by: {refs.join(', ')}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-xs font-medium text-muted-foreground">Question / step text shown to lead</p>
                    <Input
                      value={node.text}
                      onChange={(e) => updateNode(idx, { ...node, text: e.target.value } as QualificationNode)}
                      placeholder="What will the lead see?"
                    />
                  </div>
                </div>

                {node.type === 'yes_no' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">If lead answers Yes, go to</p>
                      <Select
                        value={node.next.yes}
                        onValueChange={(v) => updateNode(idx, { ...node, next: { ...node.next, yes: v } } as QualificationNode)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select next step" />
                        </SelectTrigger>
                        <SelectContent>
                          {nodeIds.map((id) => (
                            <SelectItem key={id} value={id}>
                              {id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">If lead answers No, go to</p>
                      <Select
                        value={node.next.no}
                        onValueChange={(v) => updateNode(idx, { ...node, next: { ...node.next, no: v } } as QualificationNode)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select next step" />
                        </SelectTrigger>
                        <SelectContent>
                          {nodeIds.map((id) => (
                            <SelectItem key={id} value={id}>
                              {id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {node.type === 'single_select' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-muted-foreground">
                        Options ({node.options.length}/{MAX_SINGLE_SELECT_OPTIONS})
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addSingleSelectOption(idx, node)}
                        disabled={node.options.length >= MAX_SINGLE_SELECT_OPTIONS}
                      >
                        Add option
                      </Button>
                    </div>

                    {node.options.map((opt, optionIndex) => (
                      <div key={`${node.id}-opt-${optionIndex}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end rounded-md border p-3">
                        <div className="md:col-span-3">
                          <p className="text-xs font-medium text-muted-foreground">Option value</p>
                          <Input
                            value={opt.value}
                            onChange={(e) =>
                              updateNode(idx, updateSingleSelectOption(node, optionIndex, { value: e.target.value }))
                            }
                          />
                        </div>
                        <div className="md:col-span-4">
                          <p className="text-xs font-medium text-muted-foreground">Option label</p>
                          <Input
                            value={opt.label}
                            onChange={(e) =>
                              updateNode(idx, updateSingleSelectOption(node, optionIndex, { label: e.target.value }))
                            }
                          />
                        </div>
                        <div className="md:col-span-4">
                          <p className="text-xs font-medium text-muted-foreground">Go to step</p>
                          <Select
                            value={opt.nextNodeId}
                            onValueChange={(v) =>
                              updateNode(idx, updateSingleSelectOption(node, optionIndex, { nextNodeId: v }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select next step" />
                            </SelectTrigger>
                            <SelectContent>
                              {nodeIds.map((id) => (
                                <SelectItem key={id} value={id}>
                                  {id}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={node.options.length <= 1}
                            onClick={() => removeSingleSelectOption(idx, node, optionIndex)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-md border p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Allow custom answer text</p>
                          <p className="text-xs text-muted-foreground">
                            Lets the lead type their own answer instead of picking only listed options.
                          </p>
                        </div>
                        <Switch
                          checked={Boolean(node.allowFreeText)}
                          onCheckedChange={(checked) =>
                            updateNode(
                              idx,
                              {
                                ...node,
                                allowFreeText: checked,
                                freeTextNextNodeId: checked
                                  ? node.freeTextNextNodeId || nodeIds[0] || ''
                                  : undefined,
                              } as QualificationNode
                            )
                          }
                        />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">If custom text is entered, go to</p>
                        <Select
                          value={node.freeTextNextNodeId || ''}
                          onValueChange={(v) =>
                            updateNode(idx, { ...node, freeTextNextNodeId: v } as QualificationNode)
                          }
                          disabled={!node.allowFreeText}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select next step" />
                          </SelectTrigger>
                          <SelectContent>
                            {nodeIds.map((id) => (
                              <SelectItem key={id} value={id}>
                                {id}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {node.type === 'terminal' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Final outcome</p>
                      <Select
                        value={node.terminalAction}
                        onValueChange={(v) => updateNode(idx, { ...node, terminalAction: v as any } as QualificationNode)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select outcome" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="continue_to_chat">Allow chat</SelectItem>
                          <SelectItem value="redirect_support">Send to support ticket</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-muted-foreground">Message shown to lead</p>
                      <Textarea
                        value={node.terminalMessage || ''}
                        onChange={(e) => updateNode(idx, { ...node, terminalMessage: e.target.value } as QualificationNode)}
                        className="min-h-[90px]"
                        placeholder={
                          node.terminalAction === 'redirect_support'
                            ? 'Tell the lead how to continue in support tickets'
                            : 'Optional'
                        }
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

